import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { cssMap } from '@compiled/react';

import Heading from '@atlaskit/heading';
import { Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { usePublish } from '@atlaskit/rovo-triggers';

import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';

import { QuestionItem } from './QuestionItem.tsx';
import type { PopularQuestion, Source } from './popularQuestionsTypes.ts';

type PopularQuestionsProps = {
	spaceKey: string;
};

export const PopularQuestions = ({ spaceKey }: PopularQuestionsProps) => {
	const { formatMessage } = useIntl();
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const publish = usePublish('ai-mate');

	const handleToggle = useCallback((id: string) => {
		setExpandedId((prev) => (prev === id ? null : id));
	}, []);

	const formatSourcesContext = useCallback(
		(sources: Source[]) => {
			if (sources.length === 0) {
				return '';
			}
			const sourceLines = sources.map(
				(source) => `- [${source.title}](${window.location.origin}${source.url})`,
			);
			return `\n\n${formatMessage(i18n.sourcesLabel)}\n${sourceLines.join('\n')}`;
		},
		[formatMessage],
	);

	const createSpaceContext = useCallback(
		(parentQuestion: PopularQuestion) => {
			const spaceOverviewUrl = `${window.location.origin}${SPACE_OVERVIEW.toUrl({ spaceKey })}`;

			const humanMessageContent = formatMessage(i18n.humanMessagePrompt, {
				link: spaceOverviewUrl,
				question: parentQuestion.question,
			});
			const agentMessageContent = `${parentQuestion.answer}${formatSourcesContext(parentQuestion.sources)}`;

			return {
				human_message: {
					content: humanMessageContent,
				},
				agent_message: {
					content: agentMessageContent,
				},
			};
		},
		[spaceKey, formatSourcesContext, formatMessage],
	);

	const publishRovoChat = useCallback(
		(questionText: string, parentQuestion: PopularQuestion) => {
			const context = createSpaceContext(parentQuestion);

			publish({
				type: 'chat-new',
				source: 'space-overview-popular-questions',
				data: {
					name: questionText.substring(0, 30),
					dialogues: [context],
					prompt: questionText,
				},
			});
		},
		[publish, createSpaceContext],
	);

	const handleFollowUp = useCallback(
		(followUpQuestion: string, parentQuestion: PopularQuestion) =>
			publishRovoChat(followUpQuestion, parentQuestion),
		[publishRovoChat],
	);

	const handleAskRovo = useCallback(
		(customQuestion: string, parentQuestion: PopularQuestion) =>
			publishRovoChat(customQuestion, parentQuestion),
		[publishRovoChat],
	);

	if (MOCK_QUESTIONS.length === 0) {
		return null;
	}

	return (
		<section css={styles.container} aria-label={formatMessage(i18n.heading)}>
			<Heading size="medium" css={styles.heading}>
				{formatMessage(i18n.heading)}
			</Heading>
			<Stack space="space.100">
				{MOCK_QUESTIONS.map((question) => (
					<QuestionItem
						key={question.id}
						question={question}
						isExpanded={expandedId === question.id}
						onToggle={handleToggle}
						onFollowUp={handleFollowUp}
						onAskRovo={handleAskRovo}
					/>
				))}
			</Stack>
		</section>
	);
};

const i18n = defineMessages({
	heading: {
		id: 'space-overview.smart-space-overview.popular-questions.heading',
		defaultMessage: 'Popular questions',
		description: 'Heading for the popular questions section on smart space overview.',
	},
	humanMessagePrompt: {
		id: 'space-overview.smart-space-overview.popular-questions.human-message-prompt',
		defaultMessage: 'Question about space {link}: {question}',
		description: 'The initial human prompt sent to the chat when a popular question is selected.',
	},
	sourcesLabel: {
		id: 'space-overview.smart-space-overview.popular-questions.sources-label',
		defaultMessage: 'Sources:',
		description: 'Label for the sources section in the agent response.',
	},
});

const styles = cssMap({
	container: {
		paddingBlock: token('space.300'),
	},
	heading: {
		margin: '0',
		paddingBlockEnd: token('space.200'),
	},
});

// TODO: Query for real backend data once endpoint is available
const MOCK_QUESTIONS: PopularQuestion[] = [
	{
		id: '1',
		question: 'What is our authentication migration timeline?',
		answer:
			'Our authentication migration will follow a phased rollout to minimize risk and disruption. We will complete discovery and design in Q1, build and test the new framework in Q2, begin a pilot in early Q3, and expand migration in waves through late Q3 and Q4. Legacy authentication systems will be decommissioned once adoption and stability targets are met.',
		sources: [
			{
				title: 'Authentication Migration Plan – FY Roadmap (Co…',
				url: '/wiki/spaces/ENG/pages/auth-migration-plan',
				product: 'Confluence',
				description:
					'Background In the original plan for the deployment of JCICD Portal Frontend as a Bifrost static application, the service uses Atlassian ID to authenticate the user. This authentication path is strongly recommended by Bifrost developer documentation and is employed by many internal platforms, such as Atlassian Elevate and Apex Wrapped.',
			},
			{
				title: 'Identity Platform Upgrade – Architecture Overvie…',
				url: '/wiki/spaces/ENG/pages/identity-platform-upgrade',
				product: 'Jira',
				description:
					'Latest updates Atlassian’s Identity Platform is used by Atlassian cloud products and services to accurately verify who someone is (authentication) and to determine what they have access to (authorization).',
			},
			{
				title: 'Security Assistant (Trust Eng) – Internal Apps…',
				url: '/wiki/spaces/ENG/pages/security-assistant',
				product: 'Loom',
				description:
					'This is the main structured tracker for People & Legal Tech internal services moving onto the Okta paved path (UCT + rollcall, etc.). Key points:',
			},
		],
		followUpQuestions: [
			'What risks or dependencies could impact the migration timeline?',
			'How will customers be notified about authentication changes?',
		],
	},
	{
		id: '2',
		question: 'What are the Q1 2026 product priorities?',
		answer:
			'The Q1 2026 priorities focus on platform stability improvements, rolling out the new design system, and launching the AI-powered search experience to all customers.',
		sources: [
			{
				title: 'Q1 2026 Product Roadmap',
				url: '/wiki/spaces/PROD/pages/q1-2026-roadmap',
				product: 'Confluence',
			},
		],
		followUpQuestions: [
			'Which teams are responsible for each priority?',
			'What are the key milestones for Q1?',
		],
	},
	{
		id: '3',
		question: 'How do I access the new design system components?',
		answer:
			'The new design system components are available through the shared component library. You can install them via the internal package registry and reference the Storybook documentation for usage examples.',
		sources: [
			{
				title: 'Design System Component Library',
				url: '/wiki/spaces/DESIGN/pages/component-library',
			},
		],
		followUpQuestions: ['What components are available in the latest release?'],
	},
];
